import React, { useState } from 'react';
import { Card, CardHeader, CardBody, Col } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';
import AppLogo from '../../../assets/img/brand/logo.jpeg';
import { authActionTypes } from '@store/Auth/Auth.actionTypes';
import authService from '@services/auth';
import { Button, buttonVariants } from '@components/Button';

const VerifyEmail = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { oobCode } = queryString.parse(location.search);

  const [status, setStatus] = useState(oobCode ? 'idle' : 'error');
  const [errorMessage, setErrorMessage] = useState(
    oobCode ? '' : 'Invalid verification link. Please check your email.'
  );

  // Only runs on the button click, not page load, so an email scanner
  // prefetching this link can't burn the code before the user gets here.
  const handleConfirm = async () => {
    setStatus('processing');

    const verifyResult = await authService.confirmEmailVerification({
      oobCode,
    });
    if (!verifyResult.success) {
      setErrorMessage(
        verifyResult.data ||
          'Verification failed. The link may have expired or already been used.'
      );
      setStatus('error');
      return;
    }

    const signInResult = await authService.signInWithCustomToken(
      verifyResult.data.customToken
    );
    if (!signInResult.success) {
      setErrorMessage(
        'Your email is verified, but we could not sign you in automatically. Please sign in below.'
      );
      setStatus('error');
      return;
    }

    // No need to redirect manually — the app already sends a signed-in
    // user wherever they need to go once this loads.
    dispatch({ type: authActionTypes.LOAD_CURRENT_ACCOUNT });
    setStatus('signing-in');
  };

  return (
    <Col lg="5" md="7">
      <Card className="bg-secondary shadow border-0">
        <CardHeader className="bg-transparent pb-2 border-0">
          <div className="text-center">
            <img className="auth-app-logo" src={AppLogo} alt="Prodaff" />
          </div>
          <div className="text-muted text-center mt-2 mb-3">
            {status === 'idle' && (
              <>
                <h2>Confirm your email</h2>
                <small>Click below to verify your email and continue.</small>
              </>
            )}
            {status === 'processing' && (
              <>
                <h2>Verifying your email</h2>
                <small>Please wait a moment...</small>
              </>
            )}
            {status === 'signing-in' && (
              <>
                <h2>Email verified</h2>
                <small>Signing you in...</small>
              </>
            )}
            {status === 'error' && (
              <>
                <h2>Verification failed</h2>
                <small>{errorMessage}</small>
              </>
            )}
          </div>
        </CardHeader>
        {status === 'idle' && (
          <CardBody className="px-lg-5 py-lg-4 text-center">
            <Button
              variant={buttonVariants.primary_light}
              text="Confirm email & continue"
              onClick={handleConfirm}
            />
          </CardBody>
        )}
        {status === 'error' && (
          <CardBody className="px-lg-5 py-lg-4 text-center">
            <Link to="/auth/login" className="btn btn-primary">
              Sign In
            </Link>
          </CardBody>
        )}
      </Card>
      {status === 'error' && (
        <div className="pb-5 pt-2 text-center">
          <small className="text-white">
            Need a new link?{' '}
            <Link className="font-weight-bold text-white" to="/auth/sign-up">
              Sign up again
            </Link>
          </small>
        </div>
      )}
      {status === 'signing-in' && (
        <div className="pb-5 pt-2 text-center">
          <small className="text-white">
            Taking a while?{' '}
            <Link className="font-weight-bold text-white" to="/auth/login">
              Sign in manually
            </Link>
          </small>
        </div>
      )}
    </Col>
  );
};

export default VerifyEmail;
